import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { FileSignature, Search, CheckCircle2, Clock, XCircle, AlertTriangle, ChevronLeft, MoreHorizontal, Send } from 'lucide-react';
import { toast } from 'sonner';
import { placementDocsAPI, cyclesAPI, contractsAPI } from '../lib/api';
import PlacementDetailsDialog from '../components/PlacementDetailsDialog';
import CyclePickerGrid from '../components/CyclePickerGrid';
import { usePagination, PaginationBar } from '../components/PaginationBar';

const DocPill = ({ doc }) => {
  const s = (doc?.status || 'not_created').toLowerCase();
  const map = {
    signed: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2, label: 'Signed' },
    viewed: { cls: 'bg-sky-100 text-sky-800 border-sky-200', Icon: CheckCircle2, label: 'Viewed' },
    sent: { cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock, label: 'Sent' },
    draft: { cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Clock, label: 'Draft' },
    voided: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle, label: 'Voided' },
    not_created: { cls: 'bg-white text-gray-400 border-dashed border-gray-300', Icon: XCircle, label: 'Not created' },
  };
  const cfg = map[s] || map.not_created;
  const Icon = cfg.Icon;

  // Build a compact hover timeline from whatever fields the API surfaced.
  const fmt = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };
  const rows = [];
  if (doc?.sent_at) rows.push([`Sent${doc.sent_to ? ` to ${doc.sent_to}` : ''}`, fmt(doc.sent_at)]);
  if (doc?.delivered_at) rows.push(['Email delivered', fmt(doc.delivered_at)]);
  if (doc?.opened_at) rows.push(['Email opened', fmt(doc.opened_at)]);
  if (doc?.clicked_at) rows.push(['Signing link clicked', fmt(doc.clicked_at)]);
  if (doc?.viewed_at) rows.push([doc?.signer_ip ? `Contract page opened · ${doc.signer_ip}` : 'Contract page opened', fmt(doc.viewed_at)]);
  if (doc?.signed_at) rows.push([`Signed${doc.signed_by ? ` by ${doc.signed_by}` : ''}`, fmt(doc.signed_at)]);
  if (doc?.bounced_at) rows.push(['Email bounced', fmt(doc.bounced_at)]);
  const hasTimeline = rows.length > 0;

  return (
    <div className="relative inline-block group">
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.cls} ${hasTimeline ? 'cursor-help' : ''}`}
      >
        <Icon className="w-3 h-3" /> {cfg.label}
      </span>
      {hasTimeline && (
        <div
          className="pointer-events-none absolute z-30 left-1/2 -translate-x-1/2 mt-1.5 w-64 rounded-md border border-gray-200 bg-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          role="tooltip"
        >
          <div className="px-3 py-2 border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            Document timeline
          </div>
          <ul className="px-3 py-2 space-y-1.5 text-[11px]">
            {rows.map(([label, when], i) => (
              <li key={i} className="flex items-start justify-between gap-2">
                <span className="text-gray-700 leading-snug break-words">{label}</span>
                <span className="text-gray-400 whitespace-nowrap shrink-0">{when}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const PlacementDocuments = ({ scopedCycleId, hideLayout = false, defaultStatus = 'all' }) => {
  const [rows, setRows] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState(defaultStatus); // all|complete|outstanding
  const [detailsPlacementId, setDetailsPlacementId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // When embedded (scopedCycleId prop) we always operate on that cycle and
  // hide the cycle picker + breadcrumb. Otherwise fall back to the URL param.
  const cycleParam = scopedCycleId || (searchParams.get('cycleId') || '');
  const embedded = Boolean(scopedCycleId);

  useEffect(() => { reload(); }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        placementDocsAPI.overview(),
        cyclesAPI.list(),
      ]);
      setRows(r.data || []);
      setCycles(c.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load documents overview');
    } finally {
      setLoading(false);
    }
  };

  const isComplete = (row) =>
    row.documents.eta.status === 'signed' &&
    row.documents.placement_letter.status === 'signed' &&
    row.documents.industry_letter.status === 'signed';

  // Placements scoped to the currently drilled-in cycle (or the "unassigned" bucket).
  const cycleScopedRows = useMemo(() => {
    if (!cycleParam) return rows;
    if (cycleParam === 'unassigned') return rows.filter((r) => !r.cycle_id);
    return rows.filter((r) => r.cycle_id === cycleParam);
  }, [rows, cycleParam]);

  const unassignedCount = useMemo(
    () => rows.filter((r) => !r.cycle_id).length,
    [rows],
  );

  const selectedCycle = useMemo(
    () => (cycleParam && cycleParam !== 'unassigned' ? cycles.find((c) => c.id === cycleParam) : null),
    [cycles, cycleParam],
  );

  // Feed CyclePickerGrid a lightweight "placement" shape so it can compute
  // progress bars using the same helpers as the Placements page.
  const cyclePlacementsForGrid = useMemo(
    () => rows.map((r) => ({
      id: r.placement_id,
      cycle_id: r.cycle_id || null,
      restaurant_id: r.restaurant_id,
      student_id: r.student_id,
      status: r.placement_status || 'active',
    })),
    [rows],
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return cycleScopedRows.filter((r) => {
      if (query && !(`${r.student_name} ${r.restaurant_name} ${r.position} ${r.student_number}`.toLowerCase().includes(query))) return false;
      if (statusFilter === 'complete' && !isComplete(r)) return false;
      if (statusFilter === 'outstanding' && isComplete(r)) return false;
      return true;
    });
  }, [cycleScopedRows, q, statusFilter]);
  const pg = usePagination(filtered, 'documents');

  const stats = useMemo(() => {
    const total = cycleScopedRows.length;
    const complete = cycleScopedRows.filter(isComplete).length;
    return { total, complete, outstanding: total - complete };
  }, [cycleScopedRows]);

  // Fire off a per-doc send / resend. `doc` is the DocumentStatus subshape
  // from placementDocsAPI.overview() — needs `.instance_id`. `label` is the
  // human-friendly doc name shown in the toast.
  const [sendingId, setSendingId] = useState(null);
  const sendOne = async (doc, label) => {
    if (!doc?.instance_id) {
      toast.error(`No ${label} draft to send. Generate one first via the bulk actions.`);
      return;
    }
    setSendingId(doc.instance_id);
    try {
      await contractsAPI.send(doc.instance_id, { base_url: window.location.origin });
      toast.success(`${label} sent`);
      reload();
    } catch (e) {
      // Cloudflare returns an HTML "Bad gateway" page on 502s from a Resend
      // failure. Guard against that so we never blast Cloudflare boilerplate
      // into the toast; only surface a real string detail.
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : `Failed to send ${label}`;
      toast.error(msg);
    } finally {
      setSendingId(null);
    }
  };

  // A doc can be sent/resent while its instance exists and it hasn't been
  // signed (once signed it's final; voided drafts also can't be sent).
  const canSend = (doc) => Boolean(
    doc?.instance_id && !['signed', 'voided'].includes((doc.status || '').toLowerCase())
  );
  const sendLabel = (doc) => (doc?.sent_at ? 'Resend' : 'Send');

  const body = (
    <div data-testid="placement-docs-page" className="space-y-6 animate-fade-in">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            {cycleParam ? (
              <>
                <button
                  type="button"
                  data-testid="docs-back-to-cycles"
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-ica-navy hover:underline mb-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back to cycles
                </button>
                <h1 className="font-heading text-3xl font-bold text-ica-navy" data-testid="docs-breadcrumb">
                  <span className="text-gray-400 font-normal">Compliance ›</span>{' '}
                  {cycleParam === 'unassigned' ? 'Unassigned' : (selectedCycle?.name || 'Cycle')}
                </h1>
                <p className="text-gray-500 mt-1">
                  Track who has signed the Experiential Training Agreement, Placement Letter, and Industry Placement Letter for every placement.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-bold text-ica-navy">Compliance</h1>
                <p className="text-gray-500 mt-1">
                  Pick a placement cycle to review its outstanding documents.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {(!embedded && !cycleParam) ? (
        loading ? (
          <div className="py-10 text-center text-gray-500">Loading…</div>
        ) : (
          <CyclePickerGrid
            cycles={cycles}
            placements={cyclePlacementsForGrid}
            unassignedCount={unassignedCount}
            onSelect={(cid) => {
              const next = new URLSearchParams(searchParams);
              next.set('cycleId', cid == null ? 'unassigned' : cid);
              setSearchParams(next, { replace: false });
            }}
            testIdPrefix="docs-cycle"
          />
        )
      ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card><CardContent className="pt-5">
            <div className="text-xs text-gray-500 uppercase">Total placements</div>
            <div className="text-3xl font-heading font-bold text-ica-navy">{stats.total}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <div className="text-xs text-gray-500 uppercase">Fully signed</div>
            <div className="text-3xl font-heading font-bold text-emerald-600">{stats.complete}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <div className="text-xs text-gray-500 uppercase">Outstanding</div>
            <div className="text-3xl font-heading font-bold text-amber-600">{stats.outstanding}</div>
          </CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <FileSignature className="w-5 h-5" /> Documents by placement
            </CardTitle>
            <CardDescription>
              Click any row to open the full placement details. Hover a status pill to see the signer and date.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="docs-search"
                  placeholder="Search by student, restaurant, position…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="docs-status-filter" className="sm:w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All placements</SelectItem>
                  <SelectItem value="complete">Fully signed</SelectItem>
                  <SelectItem value="outstanding">Outstanding</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="py-10 text-center text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-gray-500 italic">No placements match the current filters.</div>
            ) : (
              <>
              <PaginationBar {...pg.bar} position="top" noun="placements" />
              <div className="table-scroll">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2 px-3">Student</th>
                      <th className="text-left py-2 px-3">Restaurant</th>
                      <th className="text-left py-2 px-3">Period</th>
                      <th className="text-left py-2 px-3">Experiential Training Agreement <span className="normal-case text-[10px] text-gray-400">(restaurant)</span></th>
                      <th className="text-left py-2 px-3">Student Placement Letter <span className="normal-case text-[10px] text-gray-400">(student)</span></th>
                      <th className="text-left py-2 px-3">Industry Letter <span className="normal-case text-[10px] text-gray-400">(chef)</span></th>
                      <th className="text-right py-2 px-3 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((r) => {
                      const anySendable = canSend(r.documents.eta) || canSend(r.documents.placement_letter) || canSend(r.documents.industry_letter);
                      return (
                      <tr
                        key={r.placement_id}
                        data-testid={`doc-row-${r.placement_id}`}
                        onClick={() => setDetailsPlacementId(r.placement_id)}
                        className="border-b border-gray-100 hover:bg-ica-gold/5 cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-3">
                          <div className="font-medium text-ica-navy">{r.student_name}</div>
                          <div className="text-[11px] text-gray-500">{r.student_number || '—'} · {r.student_year || '—'}</div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="text-gray-800 flex items-center gap-1">
                            {r.restaurant_name}
                            {r.is_orphaned && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" aria-label="Restaurant deleted" />}
                          </div>
                          <div className="text-[11px] text-gray-500">{r.position || '—'}</div>
                        </td>
                        <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">
                          {r.start_date || '—'} → {r.end_date || '—'}
                        </td>
                        <td className="py-3 px-3"><DocPill doc={r.documents.eta} /></td>
                        <td className="py-3 px-3"><DocPill doc={r.documents.placement_letter} /></td>
                        <td className="py-3 px-3"><DocPill doc={r.documents.industry_letter} /></td>
                        <td className="py-3 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`doc-row-actions-${r.placement_id}`}
                                disabled={!anySendable || sendingId !== null}
                                className="h-8 px-2"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel>Send letters</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`doc-send-eta-${r.placement_id}`}
                                disabled={!canSend(r.documents.eta)}
                                onSelect={(e) => { e.preventDefault(); sendOne(r.documents.eta, 'Experiential Training Agreement'); }}
                              >
                                <Send className="w-3.5 h-3.5 mr-2" />
                                {sendLabel(r.documents.eta)} ETA
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`doc-send-placement-${r.placement_id}`}
                                disabled={!canSend(r.documents.placement_letter)}
                                onSelect={(e) => { e.preventDefault(); sendOne(r.documents.placement_letter, 'Student Placement Letter'); }}
                              >
                                <Send className="w-3.5 h-3.5 mr-2" />
                                {sendLabel(r.documents.placement_letter)} Student Placement Letter
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`doc-send-industry-${r.placement_id}`}
                                disabled={!canSend(r.documents.industry_letter)}
                                onSelect={(e) => { e.preventDefault(); sendOne(r.documents.industry_letter, 'Industry Letter'); }}
                              >
                                <Send className="w-3.5 h-3.5 mr-2" />
                                {sendLabel(r.documents.industry_letter)} Industry Letter
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBar {...pg.bar} position="bottom" noun="placements" />
              </>
            )}
          </CardContent>
        </Card>
        </>
        )}

        <PlacementDetailsDialog
          open={!!detailsPlacementId}
          onOpenChange={(o) => { if (!o) setDetailsPlacementId(null); }}
          placementId={detailsPlacementId}
        />
    </div>
  );

  return hideLayout || embedded ? body : <Layout>{body}</Layout>;
};

export default PlacementDocuments;
